import { useState, type ReactNode } from "react"

import { LockClosedIcon, PlusIcon } from "@heroicons/react/20/solid"
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid"

import type {
  SolarFinancePartnerOptionDto,
  SolarFundingGroupOptionDto,
  SolarOfferingInverterGroupDto,
  SolarOfferingProgramDto,
  SolarProposalProductOfferingDto,
  SolarSystemType,
} from "~/api/dto/solar-proposal-product-offering.dto"
import useSolarProductOfferingPreview from "~/api/hooks/useSolarProductOfferingPreview"
import useSolarProposalProductOffering from "~/api/hooks/useSolarProposalProductOffering"
import useUpdateSolarProduction from "~/api/hooks/useUpdateSolarProduction"
import useUpdateSolarProductOffering from "~/api/hooks/useUpdateSolarProductOffering"
import useUpdateSolarProposalMount from "~/api/hooks/useUpdateSolarProposalMount"
import {
  SOLAR_PRODUCT_OFFERING_INVALID_CODE,
  solarProposalErrorCode,
  solarProposalErrorMessages,
} from "~/api/solar-proposal-errors"
import { SelectField } from "~/components/ui-components/v2"
import { parseAxiosError } from "~/utils/parseAxiosError"

import {
  EMPTY_EQUIPMENT_DRAFT,
  fundingGroupForPartnerChange,
  selectedFinancePartner,
  selectedFundingGroup,
  solarEquipmentFormState,
  unavailableSavedPartner,
  type SolarEquipmentDraft,
  type SolarEquipmentOption,
  type SolarEquipmentSlot,
} from "./solar-equipment-form"
import { solarLenderOfFinanceType } from "./solar-finance-partner"
import { SolarConcertSellerBanner } from "./SolarConcertSellerBanner"
import CoreEquipmentSkeleton from "../BatteryOnlyProposal/setup/CoreEquipmentSkeleton"
import {
  SetupDetailSection,
  type SetupDetailRow,
} from "../ProposalSetup/SetupDetailSection"

export const PARTNER_NOT_EDITABLE_NOTICE = `Editing this finance partner isn't available in V2 yet`
export const NO_PARTNERS_NOTICE = `No finance partners are available for this proposal`
export const NO_DESIGN_NOTICE = `This proposal has no active design yet`
export const AURORA_LOCK_NOTE = `Set by the Aurora design`
export const PRODUCTION_WARNING = `Saved, but production couldn't be updated`
export const INVALID_SELECTION_TAG = `No longer available`
export const CONCERT_ARIZONA_NOTE = `Concert Finance Programs aren't offered in Arizona; the plan is allowed without one.`
export const CONCERT_BATTERY_REQUIRED_HINT = `Concert financing for PG&E in California requires battery storage.`
/** The gateway's refusal of a Concert plan with PG&E in California without a battery. */
export const CONCERT_CA_BATTERY_REQUIRED_ERROR = `Concert financing for PG&E in California requires battery storage. Choose a battery, or choose another finance partner.`
export const PARTNER_NOT_OFFERED_TAG = `Not offered`
export const FUNDING_GROUP_UNAVAILABLE_NOTICE = `The saved funding group is no longer offered. Choose another.`
export const LIGHTREACH_INELIGIBLE_LABEL = `Why LightReach isn't available`

const NOT_SET = `—`

type MountOption = { id: SolarSystemType; label: string }

/** V1's Mount choices, in V1's order. */
export const MOUNT_OPTIONS: readonly MountOption[] = [
  { id: `roof_mount`, label: `Roof` },
  { id: `ground_mount`, label: `Ground` },
  { id: `both`, label: `Both` },
]

const mountLabel = (systemType: SolarSystemType | null): string =>
  MOUNT_OPTIONS.find((option) => option.id === systemType)?.label ?? NOT_SET

const MountField = ({
  value,
  disabled,
  onChange,
}: {
  value: SolarSystemType | null
  disabled: boolean
  onChange: (systemType: SolarSystemType) => void
}): JSX.Element => (
  <section aria-label="Mount" className="flex flex-col gap-3">
    <h3 className={sectionTitleClass}>Mount</h3>
    <SelectField<MountOption>
      label="Mount"
      portal={false}
      disabled={disabled}
      options={[...MOUNT_OPTIONS]}
      value={MOUNT_OPTIONS.find((option) => option.id === value) ?? null}
      placeholder="Select a mount type"
      getKey={(option) => option.id}
      getLabel={(option) => option.label}
      onChange={(option) => onChange(option.id)}
    />
  </section>
)

/**
 * The Summary's Funding selection and Equipment selection steps are this one
 * form, split: Funding shows the finance partner and program, Equipment the
 * equipment and the Mount. The draft lives in the Summary, so a funding
 * choice carries into Equipment, whose button saves both.
 */
export type SolarSummaryEquipmentMode = {
  part: `equipment` | `funding`
  draft: SolarEquipmentDraft
  onDraftChange: (draft: SolarEquipmentDraft) => void
  /** Funding: the next step. Equipment: Confirm & open, after any save. */
  onContinue: () => void
  continueLabel: string
  /** Confirm & open is running, so nothing may change under it. */
  busy: boolean
}

const sectionTitleClass = `text-xs font-semibold tracking-wide text-muted uppercase`
const secondaryButtonClass = `rounded-md border border-border px-4 py-2 text-sm font-semibold text-foreground hover:bg-surface disabled:opacity-50`
const primaryButtonClass = `rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50`

const optionKey = (option: SolarEquipmentOption): number | string =>
  option.id ?? `none`

const OptionLabel = ({
  option,
}: {
  option: SolarEquipmentOption
}): JSX.Element => (
  <span className="flex items-center gap-2 whitespace-normal normal-case">
    <span>{option.label}</span>
    {option.recommended ? (
      <span className="rounded-full bg-success/15 px-2 py-0.5 text-xs font-semibold text-success">
        Recommended
      </span>
    ) : null}
  </span>
)

const PartnerOptionLabel = ({
  partner,
}: {
  partner: SolarFinancePartnerOptionDto
}): JSX.Element => (
  <span className="flex items-center gap-2 whitespace-normal normal-case">
    <span>{partner.label}</span>
    {partner.available ? null : (
      <span className="text-xs font-semibold text-danger group-data-focus/option:text-primary-foreground">
        {PARTNER_NOT_OFFERED_TAG}
      </span>
    )}
  </span>
)

const InvalidTag = (): JSX.Element => (
  <span className="self-start rounded-full border border-danger px-2 py-0.5 text-xs font-semibold tracking-wide text-danger uppercase">
    {INVALID_SELECTION_TAG}
  </span>
)

const SlotField = ({
  label,
  slot,
  disabled,
  description,
  placeholder,
  onChange,
}: {
  label: string
  slot: SolarEquipmentSlot
  disabled: boolean
  description?: ReactNode
  placeholder: string
  onChange: (id: number | null) => void
}): JSX.Element => (
  <div className="flex flex-col gap-1.5">
    <SelectField<SolarEquipmentOption>
      label={label}
      portal={false}
      disabled={disabled}
      description={description}
      options={slot.options}
      value={slot.value}
      placeholder={placeholder}
      getKey={optionKey}
      getLabel={(option) => option.label}
      renderOption={(option) => <OptionLabel option={option} />}
      onChange={(option) => onChange(option.id)}
    />
    {slot.invalid ? <InvalidTag /> : null}
  </div>
)

const SectionError = ({
  message,
  onRetry,
}: {
  message: string
  onRetry: () => void
}): JSX.Element => (
  <div
    role="alert"
    className="space-y-3 rounded-xl border border-danger/40 bg-danger/10 p-4"
  >
    <p className="text-sm text-foreground">{message}</p>
    <button
      type="button"
      className="text-sm font-semibold text-primary underline"
      onClick={onRetry}
    >
      Retry
    </button>
  </div>
)

const Notice = ({
  children,
  locked = false,
}: {
  children: ReactNode
  locked?: boolean
}): JSX.Element => (
  <p
    role="status"
    className="flex items-start gap-2 rounded-xl border border-border bg-surface p-4 text-sm text-foreground"
  >
    {locked ? (
      <LockClosedIcon
        aria-hidden="true"
        className="mt-0.5 size-4 shrink-0 text-muted"
      />
    ) : null}
    {children}
  </p>
)

/** The saved partner and equipment, for a configuration V2 can't edit now. */
const savedRows = (
  offering: SolarProposalProductOfferingDto,
  part: SolarSummaryEquipmentMode[`part`] | `all` = `all`,
  /** The Mount has its own field, so no row repeats it. */
  mountEditable = false,
): readonly SetupDetailRow[] => {
  const saved = offering.equipment
  const name = (value: string | null | undefined): string =>
    value?.trim() || NOT_SET
  const partner: SetupDetailRow = {
    label: `Finance partner`,
    value: name(offering.partner.label ?? offering.partner.financeType),
  }
  const fundingGroup: SetupDetailRow[] = offering.selectedFundingGroup
    ? [{ label: `Funding group`, value: offering.selectedFundingGroup }]
    : []
  if (part === `funding`) {
    return [partner, ...fundingGroup]
  }
  return [
    ...(part === `all` ? [partner, ...fundingGroup] : []),
    { label: `Module`, value: name(saved?.moduleName) },
    { label: `Inverter manufacturer`, value: name(saved?.inverterManufacturer) },
    { label: `Inverter model`, value: name(saved?.inverterName) },
    ...(saved?.secondaryInverterName
      ? [{ label: `Secondary inverter`, value: saved.secondaryInverterName }]
      : []),
    { label: `Battery storage`, value: name(saved?.storageName) },
    { label: `Smart panel`, value: name(saved?.smartPanelName) },
    { label: `EV charger`, value: name(saved?.evChargerName) },
    ...(part === `equipment` && !mountEditable
      ? [{ label: `Mount`, value: mountLabel(offering.systemType) }]
      : []),
  ]
}

/** The drawer's Close sits left of the action; the Summary has no Close. */
const Footer = ({
  onClose,
  children,
}: {
  onClose?: () => void
  children?: ReactNode
}): JSX.Element => (
  <div
    className={`flex items-center border-t border-border pt-5 ${onClose ? `justify-between` : `justify-end`}`}
  >
    {onClose ? (
      <button type="button" className={secondaryButtonClass} onClick={onClose}>
        Close
      </button>
    ) : null}
    {children}
  </div>
)

const programLabel = (program: SolarOfferingProgramDto): string =>
  program.required ? `${program.name} (Required)` : program.name

/**
 * Finance Partner & Equipment for a Cash, Credit Human, Concert, Sungage or LightReach Solar Proposal, after
 * Battery's equipment form: the draft keys a side-effect-free preview, so a program
 * change re-filters the equipment, and Save & Close sends the whole
 * configuration once. The server validates it again and runs the constraint
 * checks; its reasons show here and the draft stays. Saving recalculates
 * neither pricing nor savings. Another partner, a financing lock or a missing
 * design leaves the step read-only.
 */
export const SolarEquipmentStep = ({
  proposalId,
  onClose,
  onSaved,
  summary,
}: {
  proposalId: string
  /** The drawer's Close; the Summary has none. */
  onClose?: () => void
  /** A save that left nothing to retry. */
  onSaved: () => void
  summary?: SolarSummaryEquipmentMode
}): JSX.Element => {
  const query = useSolarProposalProductOffering(proposalId)
  const update = useUpdateSolarProductOffering()
  const production = useUpdateSolarProduction()
  const mount = useUpdateSolarProposalMount()
  const [ownDraft, setOwnDraft] = useState<SolarEquipmentDraft>(
    EMPTY_EQUIPMENT_DRAFT,
  )
  const draft = summary?.draft ?? ownDraft
  const writeDraft = (next: SolarEquipmentDraft): void => {
    if (summary) {
      summary.onDraftChange(next)
    } else {
      setOwnDraft(next)
    }
  }
  const part = summary?.part ?? `all`
  /** After a save that left nothing to retry: the drawer closes, the Summary moves on. */
  const finish = summary ? summary.onContinue : onSaved
  // This save couldn't update production: its Retry stands in for the form.
  // Reopened later, the step shows the form; the Updates panel keeps the retry.
  const [productionWarning, setProductionWarning] = useState(false)

  const offering = query.data
  const editable =
    offering != null &&
    offering.partner.editable &&
    !offering.financingLocked &&
    !offering.noActiveDesign &&
    offering.partners.some((entry) => entry.available)
  const partner = offering ? selectedFinancePartner(offering, draft) : null
  const partnerSelected = partner !== null
  // Sungage asks PowurOS with its funding group's type, so nothing is
  // previewed until a group is chosen.
  const fundingGroup = offering
    ? selectedFundingGroup(offering, draft, partner)
    : null
  const planReady =
    partnerSelected &&
    (partner.requiresFundingGroup !== true || fundingGroup !== null)
  // Disabled until a partner is chosen; the key still needs a plan.
  const previewFinanceType = partner?.financeType ?? `cash`
  const previewFundingGroup = fundingGroup
    ? { fundingGroup: fundingGroup.value }
    : {}
  // The saved program keys the first preview; one the draft no longer
  // offers falls back to the server's effective program.
  const savedProgramId = offering?.selectedLoanProgramId ?? null
  const usingSavedProgram = draft.loanProgramId === undefined
  const primary = useSolarProductOfferingPreview(
    proposalId,
    {
      financeType: previewFinanceType,
      ...previewFundingGroup,
      loanProgramId: usingSavedProgram
        ? savedProgramId
        : (draft.loanProgramId ?? null),
    },
    { enabled: editable && planReady && !productionWarning },
  )
  // PowurOS may refuse a retired program outright (a 502) rather than leave it
  // off the list (a 400), so any failure on the saved one asks without it.
  const savedProgramRejected =
    usingSavedProgram && savedProgramId !== null && primary.isError
  const fallback = useSolarProductOfferingPreview(
    proposalId,
    {
      financeType: previewFinanceType,
      ...previewFundingGroup,
      loanProgramId: null,
    },
    { enabled: editable && planReady && savedProgramRejected },
  )
  // The saved program is unusable when the gateway refused it (a 400: e.g. a
  // required program now applies) or that answer no longer lists it. Any
  // other failure on a program still listed stands, and Retry asks again,
  // rather than a save quietly replacing it with the default.
  const savedProgramStillOffered =
    fallback.data?.programs.some((entry) => entry.id === savedProgramId) === true
  const savedProgramUnusable =
    savedProgramRejected &&
    (solarProposalErrorCode(primary.error) ===
      SOLAR_PRODUCT_OFFERING_INVALID_CODE ||
      !savedProgramStillOffered)
  const preview = savedProgramUnusable ? fallback : primary
  const previewCurrent = preview.isSuccess && !preview.isFetching

  if (!offering) {
    return query.isError ? (
      <SectionError
        message={parseAxiosError(
          query.error,
          `The finance partner and equipment could not be loaded.`,
        )}
        onRetry={() => void query.refetch()}
      />
    ) : (
      <div
        role="status"
        aria-label="Loading finance partner and equipment"
        className="h-40 animate-pulse rounded-xl bg-muted"
      />
    )
  }

  if (productionWarning) {
    return (
      <div className="flex flex-col gap-6" data-testid="solar-equipment-step">
        <div
          role="alert"
          className="space-y-3 rounded-xl border border-warning/40 bg-warning/10 p-4"
        >
          <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <ExclamationTriangleIcon
              aria-hidden="true"
              className="size-5 text-warning"
            />
            {PRODUCTION_WARNING}
          </p>
          {production.isError ? (
            <p className="text-sm text-danger">
              {parseAxiosError(
                production.error,
                `Production couldn't be updated. Try again.`,
              )}
            </p>
          ) : null}
          <button
            type="button"
            className={primaryButtonClass}
            disabled={production.isLoading}
            onClick={() =>
              production.mutate(
                { proposalId },
                {
                  onSuccess: () => {
                    setProductionWarning(false)
                    finish()
                  },
                },
              )
            }
          >
            {production.isLoading ? `Retrying…` : `Retry`}
          </button>
        </div>
        <Footer onClose={onClose} />
      </div>
    )
  }

  if (!editable) {
    // The saved partner is no longer offered and nothing else is: say so.
    const savedUnavailable = unavailableSavedPartner(offering)
    const notice = offering.noActiveDesign
      ? NO_DESIGN_NOTICE
      : !offering.partner.editable
        ? (offering.partner.reason ?? PARTNER_NOT_EDITABLE_NOTICE)
        : offering.financingLocked
          ? (offering.financingLockReason ?? `Financing locks these selections.`)
          : savedUnavailable
            ? `${savedUnavailable.unavailableReason ?? PARTNER_NOT_OFFERED_TAG}, and no other finance partner is available.`
            : NO_PARTNERS_NOTICE
    // Another partner, a lock, or an unavailable saved partner still shows what is saved.
    const showSaved =
      !offering.noActiveDesign &&
      (!offering.partner.editable ||
        offering.financingLocked ||
        savedUnavailable !== null)
    // The Summary still needs a Mount when this save isn't available; only a
    // financing lock keeps it as saved.
    const mountEditable =
      summary?.part === `equipment` && !offering.financingLocked
    const mountValue = draft.systemType ?? offering.systemType
    const mountChanged =
      draft.systemType !== undefined && draft.systemType !== offering.systemType
    const continueReadOnly = (): void => {
      if (!summary) return
      if (!mountChanged || draft.systemType === undefined) {
        summary.onContinue()
        return
      }
      mount.mutate(
        { proposalId, systemType: draft.systemType },
        {
          onSuccess: () => {
            writeDraft({ ...draft, systemType: undefined })
            summary.onContinue()
          },
        },
      )
    }
    return (
      <div className="flex flex-col gap-6" data-testid="solar-equipment-step">
        <Notice locked={offering.financingLocked}>{notice}</Notice>
        {showSaved ? (
          <SetupDetailSection
            title="Saved configuration"
            rows={savedRows(offering, part, mountEditable)}
          />
        ) : null}
        {mountEditable ? (
          <MountField
            value={mountValue}
            disabled={mount.isLoading || summary?.busy === true}
            onChange={(systemType) => {
              mount.reset()
              writeDraft({ ...draft, systemType })
            }}
          />
        ) : null}
        {mount.isError ? (
          <p role="alert" className="text-sm text-danger">
            {parseAxiosError(mount.error, `The mount could not be saved.`)}
          </p>
        ) : null}
        <Footer onClose={onClose}>
          {summary ? (
            // The confirm names anything else still missing.
            <button
              type="button"
              data-testid="solar-summary-continue"
              className={primaryButtonClass}
              disabled={
                summary.busy ||
                mount.isLoading ||
                (mountEditable && mountValue === null)
              }
              onClick={continueReadOnly}
            >
              {mount.isLoading ? `Saving…` : summary.continueLabel}
            </button>
          ) : null}
        </Footer>
      </div>
    )
  }

  const form = solarEquipmentFormState(
    offering,
    previewCurrent ? (preview.data ?? null) : null,
    draft,
  )
  const saving = update.isLoading
  const equipmentDisabled = saving || !previewCurrent

  const busy = saving || summary?.busy === true
  const equipmentLocked = equipmentDisabled || summary?.busy === true

  const edit = (patch: SolarEquipmentDraft): void => {
    if (busy) return
    update.reset()
    writeDraft({ ...draft, ...patch })
  }

  const save = (): void => {
    if (!form.body) return
    if (!form.changed) {
      if (summary) {
        summary.onContinue()
      } else {
        onClose?.()
      }
      return
    }
    update.mutate(
      { proposalId, body: form.body },
      {
        onSuccess: (saved) => {
          writeDraft(EMPTY_EQUIPMENT_DRAFT)
          if (saved.warnings.includes(`production_not_updated`)) {
            setProductionWarning(true)
            return
          }
          finish()
        },
      },
    )
  }

  // Funding moves on with its choice drafted; Equipment saves both.
  const programNeeded = form.programs.some(
    (program) => program.required || program.isDefault,
  )
  const fundingValid =
    planReady && previewCurrent && (!programNeeded || form.program !== null)

  const lender = solarLenderOfFinanceType(partner?.financeType)
  const concertSelected = lender === `concert`
  // The eligibility read judges the saved mount, EV charger and design; the
  // save judges the draft and refuses with the same reasons.
  const lightReachSelected = lender === `lightreach`
  const allSaveErrors = update.isError
    ? solarProposalErrorMessages(update.error)
    : []
  // The CA battery refusal reads beside the Battery Storage field it fixes.
  const batteryError =
    !form.batteryHidden &&
    allSaveErrors.includes(CONCERT_CA_BATTERY_REQUIRED_ERROR)
      ? CONCERT_CA_BATTERY_REQUIRED_ERROR
      : null
  // LightReach's reasons already read beside the partner they refuse.
  const lightReachReasons =
    lightReachSelected && offering.lightReach?.eligible === false
      ? offering.lightReach.reasons
      : []
  const saveErrors = allSaveErrors.filter(
    (message) =>
      message !== batteryError && !lightReachReasons.includes(message),
  )
  // The saved partner an admin, the Site's state or the design's source no
  // longer offers: shown with why, until the seller picks another.
  const unavailablePartner =
    partner === null ? unavailableSavedPartner(offering) : null
  const unavailablePartnerNotice = unavailablePartner
    ? `${unavailablePartner.unavailableReason ?? PARTNER_NOT_OFFERED_TAG}. Choose another finance partner.`
    : undefined

  return (
    <div className="flex flex-col gap-7" data-testid="solar-equipment-step">
      {part === `equipment` ? null : (
        <section
          aria-label="Finance plan & programs"
          className="flex flex-col gap-3"
        >
          <h3 className={sectionTitleClass}>Finance plan &amp; programs</h3>
          <SelectField<SolarFinancePartnerOptionDto>
            label="Finance Partner"
            portal={false}
            disabled={busy}
            options={offering.partners}
            value={partner ?? unavailablePartner}
            placeholder="Select a finance partner"
            getKey={(option) => option.financeType}
            getLabel={(option) => option.label}
            renderOption={(option) => <PartnerOptionLabel partner={option} />}
            isOptionDisabled={(option) => !option.available}
            error={unavailablePartnerNotice}
            onChange={(option) => {
              const { financeType } = option
              if (financeType === partner?.financeType) return
              // Another plan has its own Finance Programs: the server picks
              // its effective one, unless this is the saved plan again.
              edit({
                financeType,
                ...fundingGroupForPartnerChange(
                  option,
                  offering.partner.financeType,
                ),
                loanProgramId:
                  financeType === offering.partner.financeType
                    ? undefined
                    : null,
              })
            }}
          />
          {form.fundingGroupRequired ? (
            <SelectField<SolarFundingGroupOptionDto>
              label="Funding group"
              portal={false}
              disabled={busy || form.fundingGroups.length === 0}
              options={form.fundingGroups}
              value={form.fundingGroup}
              placeholder={
                form.fundingGroups.length === 0
                  ? `No funding groups available`
                  : `Select a funding group`
              }
              getKey={(group) => group.value}
              getLabel={(group) => group.label}
              error={
                form.fundingGroupUnavailable
                  ? FUNDING_GROUP_UNAVAILABLE_NOTICE
                  : undefined
              }
              onChange={(group) => {
                if (group.value === form.fundingGroup?.value) return
                const savedPlan =
                  partner?.financeType === offering.partner.financeType &&
                  group.value === offering.selectedFundingGroup
                // Each group is its own PowurOS funding type, with its own
                // Finance Programs: the server picks the effective one.
                edit({
                  fundingGroup: group.value,
                  loanProgramId: savedPlan ? undefined : null,
                })
              }}
            />
          ) : null}
          {concertSelected && offering.concert?.programsOffered === false ? (
            <p
              role="status"
              data-testid="solar-concert-arizona-note"
              className="text-sm text-muted"
            >
              {CONCERT_ARIZONA_NOTE}
            </p>
          ) : null}
          {concertSelected ? (
            <SolarConcertSellerBanner proposalId={proposalId} />
          ) : null}
          {lightReachSelected && offering.lightReach != null ? (
            <div
              data-testid="solar-lightreach-eligibility"
              className="flex flex-col gap-1.5"
            >
              <p role="note" className="text-sm text-muted">
                {offering.lightReach.note}
              </p>
              {offering.lightReach.eligible ? null : (
                <ul
                  role="status"
                  aria-label={LIGHTREACH_INELIGIBLE_LABEL}
                  className="list-disc pl-5 text-sm text-danger"
                >
                  {offering.lightReach.reasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
          {savedProgramUnusable && fallback.isSuccess ? (
            <p role="status" className="text-sm text-danger">
              The saved Finance Program is no longer offered. Choose one below.
            </p>
          ) : null}
          {planReady && form.programs.length > 0 ? (
            <div className="flex flex-col gap-1.5">
              <SelectField<SolarOfferingProgramDto>
                label="Finance Programs"
                portal={false}
                disabled={equipmentLocked}
                options={form.programs}
                value={form.program}
                placeholder="Select a Finance Program"
                getKey={(program) => program.id}
                getLabel={programLabel}
                onChange={(program) => edit({ loanProgramId: program.id })}
              />
              {/* PowurOS writes the domestic-content notice into the program's description. */}
              {form.program?.description ? (
                <p className="text-sm text-muted">{form.program.description}</p>
              ) : null}
            </div>
          ) : null}
        </section>
      )}

      {planReady && part !== `funding` ? (
        <section aria-label="Core equipment" className="flex flex-col gap-3">
          <h3 className={sectionTitleClass}>Core equipment</h3>
          {preview.isError ? (
            <SectionError
              message={parseAxiosError(
                preview.error,
                `Equipment could not be loaded.`,
              )}
              onRetry={() => void preview.refetch()}
            />
          ) : preview.isFetching || !preview.data ? (
            <CoreEquipmentSkeleton />
          ) : (
            <>
              <SlotField
                label="Module"
                slot={form.module}
                disabled={equipmentLocked || offering.moduleLocked}
                description={
                  offering.moduleLocked ? AURORA_LOCK_NOTE : undefined
                }
                placeholder="Select a module"
                onChange={(id) => id !== null && edit({ moduleId: id })}
              />
              <div className="flex flex-col gap-1.5">
                <SelectField<
                  SolarOfferingInverterGroupDto | { manufacturer: string }
                >
                  label="Inverter Manufacturer"
                  portal={false}
                  disabled={equipmentLocked || offering.invertersLocked}
                  description={
                    offering.invertersLocked ? AURORA_LOCK_NOTE : undefined
                  }
                  options={form.manufacturers}
                  value={
                    form.manufacturer ??
                    (form.manufacturerName
                      ? { manufacturer: form.manufacturerName }
                      : null)
                  }
                  placeholder="Select a manufacturer"
                  getKey={(group) => group.manufacturer}
                  getLabel={(group) => group.manufacturer}
                  onChange={(group) => {
                    if (group.manufacturer === form.manufacturerName) return
                    edit({
                      inverterManufacturer: group.manufacturer,
                      inverterId: null,
                      secondaryInverterId: null,
                    })
                  }}
                />
              </div>
              <SlotField
                label="Inverter Model"
                slot={form.inverter}
                disabled={
                  equipmentLocked ||
                  offering.invertersLocked ||
                  form.manufacturerName === null
                }
                description={
                  offering.invertersLocked ? AURORA_LOCK_NOTE : undefined
                }
                placeholder="Select an inverter"
                onChange={(id) =>
                  edit({ inverterId: id, secondaryInverterId: null })
                }
              />
              {form.secondary ? (
                <SlotField
                  label="Secondary Inverter (optional)"
                  slot={form.secondary}
                  disabled={equipmentLocked || offering.invertersLocked}
                  placeholder="None"
                  onChange={(id) => edit({ secondaryInverterId: id })}
                />
              ) : null}
              {form.batteryHidden ? null : (
                <div className="flex flex-col gap-1.5">
                  <SlotField
                    label="Battery Storage"
                    slot={form.battery}
                    disabled={equipmentLocked || form.batteryUnsupported}
                    description={
                      form.batteryUnsupported
                        ? `This inverter doesn't support a battery.`
                        : concertSelected && offering.concert?.batteryRequired
                          ? CONCERT_BATTERY_REQUIRED_HINT
                          : undefined
                    }
                    placeholder="None"
                    onChange={(id) => edit({ storageId: id })}
                  />
                  {batteryError ? (
                    <p
                      role="alert"
                      data-testid="solar-concert-battery-required"
                      className="text-sm text-danger"
                    >
                      {batteryError}
                    </p>
                  ) : null}
                </div>
              )}
            </>
          )}
        </section>
      ) : null}

      {planReady && previewCurrent && part !== `funding` ? (
        form.showOptional ? (
          <section
            aria-label="Optional equipment"
            className="flex flex-col gap-3"
          >
            <h3 className={sectionTitleClass}>Optional equipment</h3>
            <SlotField
              label="Smart Panel"
              slot={form.smartPanel}
              disabled={equipmentLocked}
              placeholder="None"
              onChange={(id) => edit({ smartPanelId: id })}
            />
            <SlotField
              label="EV Charger"
              slot={form.evCharger}
              disabled={equipmentLocked}
              placeholder="None"
              onChange={(id) => edit({ evChargerId: id })}
            />
          </section>
        ) : (
          <button
            type="button"
            className="flex items-center gap-1.5 self-start text-sm font-semibold text-primary"
            onClick={() => edit({ showOptional: true })}
          >
            <PlusIcon aria-hidden="true" className="size-4" />
            Add optional equipment (Smart Panel, EV Charger)
          </button>
        )
      ) : null}

      {part === `equipment` ? (
        <MountField
          value={form.systemType}
          disabled={busy}
          onChange={(systemType) => edit({ systemType })}
        />
      ) : null}

      {form.dropped.length > 0 && part !== `funding` ? (
        <p role="status" className="text-sm text-danger">
          No longer offered for this program, so saving removes:{` `}
          {form.dropped.join(`, `)}.
        </p>
      ) : null}

      {saveErrors.length > 0 ? (
        <div
          role="alert"
          className="space-y-1 rounded-xl border border-danger/40 bg-danger/10 p-4"
        >
          <p className="text-sm font-semibold text-foreground">
            The configuration wasn&apos;t saved:
          </p>
          <ul className="list-disc space-y-1 pl-5 text-sm text-foreground">
            {saveErrors.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </div>
      ) : update.isError && batteryError == null ? (
        <p role="alert" className="text-sm text-danger">
          {parseAxiosError(
            update.error,
            `The configuration could not be saved.`,
          )}
        </p>
      ) : null}

      <Footer onClose={onClose}>
        {summary?.part === `funding` ? (
          <button
            type="button"
            data-testid="solar-summary-continue"
            className={primaryButtonClass}
            disabled={!fundingValid || busy}
            onClick={summary.onContinue}
          >
            Continue
          </button>
        ) : summary ? (
          <button
            type="button"
            data-testid="solar-summary-continue"
            className={primaryButtonClass}
            disabled={
              !form.valid || !previewCurrent || busy || form.systemType === null
            }
            onClick={save}
          >
            {saving ? `Saving…` : summary.continueLabel}
          </button>
        ) : (
          <button
            type="button"
            className={primaryButtonClass}
            disabled={!form.valid || !previewCurrent || saving}
            onClick={save}
          >
            {saving ? `Saving…` : form.changed ? `Save & Close` : `Continue`}
          </button>
        )}
      </Footer>
    </div>
  )
}
